import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button } from '@picsio/ui';

import localization from '../../../../shared/strings';
import Checkbox from '../../../../UIComponents/checkbox';
import confirmShopifyPrepareIfNeeded, {
  PREPARE_ACTIONS,
} from '../../../../helpers/shopify/confirmShopifyPrepareIfNeeded';
import {
  isShopifyChannelOption,
  pickShopifyMediaByChannel,
} from '../../../../helpers/shopify/listingMasterConsistency';
import { aggregateSyncMediaSelectionStateByChannel } from '../../../../helpers/shopify/resolveSyncMediaSelectionState';
import Icon from '../../../Icon';
import Tooltip from '../../../Tooltip';
import ButtonLoader from '../ButtonLoader';
import ChannelTabsHeader from '../MediaSelection/ChannelTabsHeader';
import MediaThumbnailGrid from '../MediaSelection/MediaThumbnailGrid';
import getSyncMediaConfirmDisabledTooltip from '../helpers/getSyncMediaConfirmDisabledTooltip';
import { getSelectedAssetsFromMediaByChannel } from '../helpers/syncFlowMediaHelpers';
import { CHANNEL_SYNC_ACTION } from '../helpers/syncFlowOrchestrator';
import styles from '../styles.module.scss';

function ProductMediaSelectionDialog({
  channelOptions,
  selectedChannels,
  channelActionsByChannelId,
  assets,
  productMediaByChannel,
  validateBeforeFinish,
  onConfirm,
  onBack,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const mediaStrings = strings.PRODUCT_MEDIA;

  const channels = useMemo(
    () => channelOptions.filter(({ value }) => selectedChannels.includes(value)),
    [channelOptions, selectedChannels],
  );

  const [activeChannelId, setActiveChannelId] = useState(channels[0]?.value);
  const [mediaByChannel, setMediaByChannel] = useState(() => (
    selectedChannels.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: productMediaByChannel[channelId] || [],
    }), {})
  ));
  const [isConfirming, setIsConfirming] = useState(false);

  const activeChannel = channels.find(({ value }) => value === activeChannelId) || channels[0];
  const selectedAssetIds = mediaByChannel[activeChannel?.value] || [];
  const isMultiChannel = channels.length > 1;

  const allChecked = assets.length > 0 && selectedAssetIds.length === assets.length;
  const noneChecked = selectedAssetIds.length === 0;
  const selectAllValue = useMemo(() => {
    if (allChecked) return true;
    if (noneChecked) return false;
    return 'indeterminate';
  }, [allChecked, noneChecked]);

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handlePrevious = useCallback(() => {
    destroy();
    onBack?.(mediaByChannel);
  }, [destroy, mediaByChannel, onBack]);

  const handleChannelChange = useCallback((channelId) => {
    setActiveChannelId(channelId);
  }, []);

  const handleSelectionChange = useCallback((nextSelectedAssetIds) => {
    setMediaByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: nextSelectedAssetIds,
    }));
  }, [activeChannel.value]);

  const handleSelectAll = useCallback(() => {
    if (allChecked) {
      handleSelectionChange([]);
      return;
    }

    handleSelectionChange(assets.map(({ _id }) => _id));
  }, [allChecked, assets, handleSelectionChange]);

  const shopifyPrepareAssets = useMemo(
    () => getSelectedAssetsFromMediaByChannel(
      assets,
      pickShopifyMediaByChannel(mediaByChannel, channels),
    ),
    [assets, mediaByChannel, channels],
  );
  const selectionAggregate = useMemo(
    () => aggregateSyncMediaSelectionStateByChannel({
      assets,
      mediaByChannel,
      channelActionsByChannelId,
      publishAction: CHANNEL_SYNC_ACTION.PUBLISH,
      channelOptions: channels,
    }),
    [assets, mediaByChannel, channelActionsByChannelId, channels],
  );
  const isActiveShopifyChannel = isShopifyChannelOption(activeChannel);
  const requirePublishReadyForActiveChannel = (
    isActiveShopifyChannel
    && channelActionsByChannelId[activeChannel?.value] === CHANNEL_SYNC_ACTION.PUBLISH
  );
  const confirmDisabledTooltip = getSyncMediaConfirmDisabledTooltip(selectionAggregate);
  const isConfirmDisabled = selectionAggregate.hasHardSelected || isConfirming;

  const handleConfirm = useCallback(async () => {
    if (selectionAggregate.hasHardSelected) {
      return;
    }

    setIsConfirming(true);

    try {
      const prepareAction = await confirmShopifyPrepareIfNeeded(shopifyPrepareAssets);
      if (prepareAction === PREPARE_ACTIONS.ASSET_COPY) {
        // Leave sync wizard so the user can finish Asset Copy unobstructed.
        destroy();
        onCancel?.();
        return;
      }
      if (prepareAction !== PREPARE_ACTIONS.PROCEED) {
        setIsConfirming(false);
        return;
      }

      if (validateBeforeFinish) {
        try {
          await validateBeforeFinish(() => {
            setIsConfirming(false);
            destroy();
          });
          destroy();
          onConfirm(mediaByChannel);
        } catch {
          if (isDestroyedRef.current) {
            onValidationAbort?.();
            return;
          }

          setIsConfirming(false);
        }

        return;
      }

      destroy();
      onConfirm(mediaByChannel);
    } catch {
      setIsConfirming(false);
    }
  }, [
    destroy,
    onCancel,
    onConfirm,
    shopifyPrepareAssets,
    selectionAggregate.hasHardSelected,
    validateBeforeFinish,
    onValidationAbort,
    mediaByChannel,
  ]);

  const getBadgeCount = useCallback(
    (channelId) => (mediaByChannel[channelId] || []).length,
    [mediaByChannel],
  );

  const counterText = mediaStrings.COUNTER(
    selectedAssetIds.length,
    assets.length,
    activeChannel?.label || '',
  );

  const channelLabel = activeChannel?.label || '';

  const title = isMultiChannel
    ? mediaStrings.TITLE_MULTI
    : mediaStrings.TITLE_SINGLE(channelLabel);

  const description = isMultiChannel
    ? mediaStrings.DESCRIPTION_MULTI
    : mediaStrings.DESCRIPTION_SINGLE(channelLabel);

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionModalWide} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBoxWide}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <Icon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{description}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMultiChannel}>
              <ChannelTabsHeader
                channels={channels}
                activeChannelId={activeChannel.value}
                getBadgeCount={getBadgeCount}
                onChange={handleChannelChange}
              />
            </If>
            <div className={styles.connectChannelSectionMediaToolbar}>
              <Checkbox value={selectAllValue} onChange={handleSelectAll} />
              <span className={styles.connectChannelSectionMediaCounter}>{counterText}</span>
            </div>
            <div className={styles.connectChannelSectionMediaDivider} />
            <div className={styles.connectChannelSectionMediaGridWrapper}>
              <MediaThumbnailGrid
                assets={assets}
                selectedAssetIds={selectedAssetIds}
                showValidationStatus={isActiveShopifyChannel}
                requirePublishReady={requirePublishReadyForActiveChannel}
                onChange={handleSelectionChange}
              />
            </div>
          </div>
        </div>
        <div className={`simpleDialogFooter ${styles.connectChannelSectionMediaFooter}`}>
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <If condition={onBack}>
            <Button
              color="secondary"
              size="md"
              variant="contained"
              disabled={isConfirming}
              onClick={handlePrevious}
            >
              {strings.BTN_PREVIOUS}
            </Button>
          </If>
          <Tooltip content={confirmDisabledTooltip}>
            <Button
              color="primary"
              size="md"
              variant="contained"
              disabled={isConfirmDisabled}
              endIcon={isConfirming ? <ButtonLoader /> : null}
              onClick={handleConfirm}
            >
              {strings.BTN_CONFIRM_MEDIA}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

ProductMediaSelectionDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    type: PropTypes.string,
  })).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  channelActionsByChannelId: PropTypes.objectOf(PropTypes.string),
  assets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
  })).isRequired,
  productMediaByChannel: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  validateBeforeFinish: PropTypes.func,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

ProductMediaSelectionDialog.defaultProps = {
  channelActionsByChannelId: {},
  productMediaByChannel: {},
  validateBeforeFinish: undefined,
  onBack: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showProductMediaSelectionDialog(props) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <ProductMediaSelectionDialog
      {...props}
      root={root}
    />,
  );
}

export default ProductMediaSelectionDialog;
